import { useEffect, useMemo, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import isEqual from 'lodash/isEqual';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { InputGroupCardUiOptions } from './schema';
import { useCatalogUserLookup } from '../../../hooks/useCatalogUserLookup';

export { InputGroupCardSchema } from './schema';

type PropertyEntry = {
  title: string;
  description?: string;
  type?: string;
  format?: string;
  pattern?: string;
  errorMessage?: string;
};

// Field status type
type FieldStatus = 'default' | 'success' | 'error';

/**
 * InputGroupCard Field Component
 * @param props Props injected by Scaffolder for Field Extension Components
 * @returns InputGroupCard React Element
 */
export const InputGroupCard = (
  props: FieldExtensionComponentProps<object, InputGroupCardUiOptions>,
) => {
  const {
    onChange,
    formData,
    uiSchema,
    schema: { title = 'Title', description = 'Description', properties = {} },
    required,
  } = props;

  const constValues: Record<string, string> = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(properties)
          .filter(k => (properties[k] as any)?.const !== undefined)
          .map(k => [
            k,
            String(
              (properties[k] as any)?.const ??
                (properties[k] as any)?.default ??
                '',
            ),
          ]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(properties)],
  );

  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';

  const [propertiesValues, setPropertiesValues] = useState<{
    [key: string]: string;
  }>(() => {
    return {
      ...constValues,
      ...((formData as Record<string, string>) ?? {}),
    };
  });
  // per-field status
  const [propertiesStatus, setPropertiesStatus] = useState<{
    [key: string]: FieldStatus;
  }>({});
  const [propertiesHelperTexts, setPropertiesHelperTexts] = useState<{
    [key: string]: string;
  }>({});
  const [loadingKeys, setLoadingKeys] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [visiblePasswords, setVisiblePasswords] = useState<{
    [key: string]: boolean;
  }>({});
  const [focusedKeys, setFocusedKeys] = useState<{ [key: string]: boolean }>(
    {},
  );

  const uiOptions =
    (uiSchema?.['ui:options'] as InputGroupCardUiOptions | undefined) ?? {};
  const userIdKey = (uiOptions as any)?.userIdKey ?? 'userId';
  const emailKey = (uiOptions as any)?.emailKey ?? 'email';
  const namespace = (uiOptions as any)?.namespace ?? 'default';
  const debounceMs: number = (uiOptions as any)?.debounceMs ?? 1000;

  const onChangeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const { findUserByUserId, findUserByEmail } = useCatalogUserLookup({
    namespace,
  });

  // Timers to debounce lookups
  const timersRef = useRef<
    Record<string, ReturnType<typeof setTimeout> | undefined>
  >({});
  useEffect(() => {
    const timersSnapshot = timersRef.current;
    return () => {
      Object.values(timersSnapshot).forEach(t => t && clearTimeout(t));
      if (onChangeTimerRef.current) clearTimeout(onChangeTimerRef.current);
    };
  }, []);

  // Initialize from formData if local state is empty (e.g. initial empty load, then async populated)
  useEffect(() => {
    if (formData && Object.keys(propertiesValues).length === 0) {
      setPropertiesValues({
        ...constValues,
        ...(formData as Record<string, string>),
      });
    }
  }, [formData, constValues, propertiesValues]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const lastEmittedValueRef = useRef<any>(formData || null);
  const lastEmittedErrorRef = useRef<boolean | null>(null);

  const updatedValues = useMemo(
    () => ({
      ...propertiesValues,
      ...constValues,
    }),
    [propertiesValues, constValues],
  );

  const hasAnyErrors = Object.values(propertiesStatus).includes('error');

  // Synchronize propertiesValues and validation status with parent (formData/onChange)
  useEffect(() => {
    // If local state is empty but parent formData has values, wait for initialization
    const isLocalEmpty = Object.keys(propertiesValues).length === 0;
    const hasFormData =
      !!formDataRef.current && Object.keys(formDataRef.current).length > 0;
    if (isLocalEmpty && hasFormData) {
      return;
    }

    const isValueEqual = isEqual(lastEmittedValueRef.current, updatedValues);
    const isErrorEqual = lastEmittedErrorRef.current === hasAnyErrors;

    // Avoid redundant onChange on initial sync if they match exactly
    if (
      lastEmittedValueRef.current === null &&
      isEqual(formDataRef.current, updatedValues)
    ) {
      lastEmittedValueRef.current = updatedValues;
      lastEmittedErrorRef.current = hasAnyErrors;
      return;
    }

    if (!isValueEqual || !isErrorEqual) {
      if (onChangeTimerRef.current) {
        clearTimeout(onChangeTimerRef.current);
      }
      onChangeTimerRef.current = setTimeout(() => {
        lastEmittedValueRef.current = updatedValues;
        lastEmittedErrorRef.current = hasAnyErrors;
        onChangeRef.current?.(
          updatedValues,
          hasAnyErrors ? ['There are errors in the form.'] : undefined,
        );
      }, 0);
    }
  }, [propertiesValues, updatedValues, hasAnyErrors]);

  /**
   * Sets loading state for a specific field
   * @param key The property key
   * @param v The loading state
   */
  function setLoadingFor(key: string, v: boolean) {
    setLoadingKeys(prev => ({ ...prev, [key]: v }));
  }

  /**
   * Sets field status and helper text
   * @param key The property key
   * @param status The field status
   * @param text The helper text
   */
  function setFieldState(key: string, status: FieldStatus, text: string) {
    setPropertiesStatus(prev => ({ ...prev, [key]: status }));
    setPropertiesHelperTexts(prev => ({ ...prev, [key]: text }));
  }

  /**
   * Resolves the error message for a field, with uiSchema taking priority over the JSON schema property.
   * @param key The property key
   * @param fallback The default message if none is configured
   */
  function resolveErrorMessage(key: string, fallback: string): string {
    const uiMsg = (uiSchema?.[key]?.['ui:options'] as Record<string, unknown>)
      ?.errorMessage as string | undefined;
    const propMsg = (properties[key] as PropertyEntry)?.errorMessage;
    return uiMsg ?? propMsg ?? fallback;
  }

  /**
   * Validates a property value by using patterns and required status
   * @param key The property key
   * @param value The property value
   */
  function validateProperty(key: string, value: string) {
    const property = properties[key] as PropertyEntry;
    let helperText = property?.description ?? '';

    const patternMismatch =
      property?.pattern && !new RegExp(property.pattern).test(value);

    if (required) {
      if (value === '') {
        helperText = resolveErrorMessage(
          key,
          'Error. This is a required field.',
        );
        setFieldState(key, 'error', helperText);
      } else if (patternMismatch) {
        if (key === emailKey) {
          helperText = resolveErrorMessage(
            key,
            'Error. Inputted text is in an invalid format. Domain must be either @mercedes-benz.com or @extaccount.com.',
          );
          setFieldState(key, 'error', helperText);
        } else if (key === userIdKey) {
          helperText =
            property?.errorMessage ??
            'Error. Inputted text is in an invalid format. User ID must only contain alphanumeric characters (letters and numbers).';
          setFieldState(key, 'error', helperText);
        }
      } else {
        setFieldState(key, 'default', helperText);
      }
    }

    setProperty(key, value);
  }

  /**
   * Sets property value
   * @param key The property key
   * @param value The property value
   */
  function setProperty(key: string, value: string) {
    // Reset status while user types
    if (value.trim().length === 0) {
      const property = properties[key] as PropertyEntry;
      setFieldState(key, 'default', property?.description ?? '');
    }

    setPropertiesValues(prevValues => ({
      ...prevValues,
      ...constValues,
      [key]: value,
    }));
  }

  /**
   * Handles user ID lookup in catalog
   * @param value The user ID value
   */
  async function handleUserIdLookup(value: string) {
    const user = await findUserByUserId(value).finally(() =>
      setLoadingFor(userIdKey, false),
    );

    const email = (user as any)?.spec?.profile?.email ?? '';
    if (email) {
      if (propertiesValues[emailKey] !== email) {
        setProperty(emailKey, email);
      }

      setFieldState(
        userIdKey,
        'success',
        'Success! This user ID has been found in the catalog.',
      );
      setFieldState(
        emailKey,
        'success',
        'Success! This email has been obtained from the catalog.',
      );
    }

    if (user) {
      // User found but no email -> show message without error highlighting
      setFieldState(
        userIdKey,
        'default',
        'User found but no email in spec.profile.email',
      );
    } else {
      // Pattern match but user not found
      setFieldState(userIdKey, 'default', '');
    }

    validateProperty(userIdKey, value);
  }

  /**
   * Handles email lookup in catalog
   * @param value The email value
   */
  async function handleEmailLookup(value: string) {
    const user = await findUserByEmail(value).finally(() =>
      setLoadingFor(emailKey, false),
    );

    if (user) {
      const id = user.metadata?.name ?? '';
      if (id) {
        if (propertiesValues[userIdKey] !== id) {
          setProperty(userIdKey, id);
        }

        // On success: mark fields as success and set messages
        setFieldState(
          emailKey,
          'success',
          'Success! This email has been found in the catalog.',
        );
        setFieldState(
          userIdKey,
          'success',
          'Success! This user ID has been obtained from the catalog.',
        );
      }
    } else {
      setFieldState(emailKey, 'default', '');
    }

    validateProperty(emailKey, value);
  }

  /**
   * Schedules lookup based on key
   * @param key The property key
   * @param value The property value
   */
  function scheduleLookup(key: string, value: string) {
    if (key === userIdKey) {
      handleUserIdLookup(value);
    } else if (key === emailKey) {
      handleEmailLookup(value);
    }
  }

  return (
    <Box sx={{ backgroundColor, padding: 2, borderRadius: 1 }}>
      <Box component="h2" sx={{ marginTop: 0, marginBottom: 1 }}>
        {title}
      </Box>
      <Box>{description}</Box>
      <Divider sx={{ marginBottom: 1, marginTop: 1 }} />

      {Object.entries(properties as Record<string, PropertyEntry>)
        .filter(
          ([key]) =>
            (properties[key] as any)?.const === undefined &&
            uiSchema?.[key]?.['ui:widget'] !== 'hidden',
        )
        .map(([key, property]) => {
          const placeholder = uiSchema?.[key]?.['ui:placeholder'];
          const status = propertiesStatus[key] ?? 'default';
          const isPasswordField = uiSchema?.[key]?.['ui:widget'] === 'password';
          const isPasswordVisible = visiblePasswords[key] ?? false;

          /**
           * Resolves the input type based on format and password widget
           */
          let resolvedType: string;
          if (isPasswordField && isPasswordVisible) {
            resolvedType = 'text';
          } else if (isPasswordField) {
            resolvedType = 'password';
          } else if (property.format === 'email') {
            resolvedType = 'email';
          } else {
            resolvedType = 'text';
          }

          return (
            <Box key={key} sx={{ marginTop: 2 }}>
              <TextField
                fullWidth
                label={property.title}
                placeholder={placeholder}
                InputLabelProps={{
                  shrink: focusedKeys[key] || !!propertiesValues[key],
                }}
                required={required as any}
                onFocus={() =>
                  setFocusedKeys(prev => ({ ...prev, [key]: true }))
                }
                onBlur={() =>
                  setFocusedKeys(prev => ({ ...prev, [key]: false }))
                }
                value={propertiesValues[key] ?? ''}
                sx={{
                  'input::-ms-reveal, input::-ms-clear, input::-webkit-credentials-auto-fill-button, input::-webkit-textfield-decoration-container':
                    {
                      display: 'none',
                    },
                }}
                onChange={e => {
                  const v = e.currentTarget.value;

                  if (key === userIdKey || key === emailKey) {
                    setProperty(key, v);
                    if (v.trim().length > 0) {
                      setLoadingFor(key, true);
                      setFieldState(
                        key,
                        'default',
                        'Searching for user in catalog...',
                      );

                      if (timersRef.current[key]) {
                        clearTimeout(timersRef.current[key]);
                      }

                      timersRef.current[key] = setTimeout(() => {
                        scheduleLookup(key, v);
                      }, debounceMs);
                    }
                  } else {
                    // Validate pattern immediately for non-lookup fields
                    const prop = properties[key] as PropertyEntry;
                    if (
                      v.trim().length > 0 &&
                      prop?.pattern &&
                      !new RegExp(prop.pattern).test(v)
                    ) {
                      setFieldState(
                        key,
                        'error',
                        resolveErrorMessage(
                          key,
                          'Error. Inputted text is in an invalid format.',
                        ),
                      );
                    } else {
                      setFieldState(key, 'default', prop?.description ?? '');
                    }
                    setProperty(key, v);
                  }
                }}
                type={resolvedType}
                variant="outlined"
                // control red error state
                error={status === 'error'}
                // set green color when success
                color={status === 'success' ? 'success' : undefined}
                // keep green visible without focus
                focused={status === 'success'}
                helperText={
                  propertiesHelperTexts[key] ?? property.description ?? ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {loadingKeys[key] && <CircularProgress size={18} />}
                      {isPasswordField && !!propertiesValues[key] && (
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setVisiblePasswords(prev => ({
                              ...prev,
                              [key]: !prev[key],
                            }))
                          }
                          edge="end"
                          size="small"
                        >
                          {isPasswordVisible ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          );
        })}
    </Box>
  );
};
